import {
  PerfusionScanModel,
  type IPerfusionScansResponse
} from '@/models/perfusionScan';
import { signedApiClient } from './apiClient';

type PerfusionScansPayload = {
  scans: PerfusionScanModel[];
  total: number;
};

/** Mirrors the backend's own page size, so a case with more scans than this pages explicitly. */
const DEFAULT_SCAN_LIMIT = 50;

class PerfusionScanService {
  private readonly pumpEndpoint: string = 'pump';

  /** `GET pump/:pumpCenterId/case/:perfusionId/scan` — the case's scans, newest first. */
  async getScans(
    pumpCenterId: string,
    perfusionId: string,
    limit: number = DEFAULT_SCAN_LIMIT,
    offset: number = 0
  ): Promise<PerfusionScansPayload> {
    // The signature covers the request line, so the query string has to be part of the signed uri.
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan?limit=${limit}&offset=${offset}`;
    const apiClient = signedApiClient('GET', uri);
    const response = await apiClient.get(uri);
    const responseData = response.data as IPerfusionScansResponse;

    return {
      scans: (responseData.scans ?? []).map(PerfusionScanModel.mapFromBackend),
      total: responseData.total ?? 0
    };
  }

  /**
   * `GET pump/:pumpCenterId/case/:perfusionId/scan/:scanId/frame/:frameIndex` as an object URL.
   *
   * The frames live in a private bucket, so they are fetched through the signed API like any other
   * blob rather than linked to directly. Callers own the returned URL and must revoke it.
   *
   * `width` asks the backend to resize on the way out — use it for thumbnails, omit it for the
   * full-resolution capture.
   */
  async getFrameUrl(
    pumpCenterId: string,
    perfusionId: string,
    scanId: string,
    frameIndex: number,
    width?: number
  ): Promise<string> {
    const query = width ? `?width=${width}` : '';
    const uri = `${this.pumpEndpoint}/${pumpCenterId}/case/${perfusionId}/scan/${scanId}/frame/${frameIndex}${query}`;
    const apiClient = signedApiClient('GET', uri);
    // Passed per request rather than set on the shared client's defaults, which would leak into
    // every other call made while this one is in flight.
    const response = await apiClient.get(uri, { responseType: 'blob' });

    return window.URL.createObjectURL(response.data as Blob);
  }
}

export default new PerfusionScanService();
